import { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import toast from "react-hot-toast";
import { DataTableNew } from "@/components/shared/DataTable";
import SearchInput from "@/components/shared/SearchInput";
import { RoleService } from "@/services/roleService";
import { PermissionsService } from "@/services/permissionsService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

// Read-only role x permission matrix, built on the shared DataTableNew:
// roles are the (backend-paginated) rows, and one column per permission is
// generated from the global permission catalogue, which is fetched once up
// front since it doesn't belong to a single tenant. Columns carry the
// permission's name, never its code: a SuperAdmin reads this in the
// product's words.
const permissionLabel = (permission) => permission?.displayName || permission?.code || "";
const RolesPanel = ({ tenantId }) => {
    const [permissions, setPermissions] = useState([]);
    const [isPermissionsLoading, setIsPermissionsLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        let cancelled = false;

        const fetchPermissions = async () => {
            setIsPermissionsLoading(true);
            try {
                const res = await PermissionsService.fetchPermissionsList({ pagination: false });
                if (cancelled) return;

                const rows = res?.data?.permissions ?? [];
                // Grouped by category, so related columns sit side by side.
                setPermissions(
                    [...rows].sort(
                        (a, b) =>
                            (a?.category ?? "").localeCompare(b?.category ?? "") ||
                            permissionLabel(a).localeCompare(permissionLabel(b)),
                    ),
                );
            } catch (e) {
                if (cancelled) return;
                console.error("Failed to fetch permission catalogue:", e);
                toast.error(apiErrorMessageFormat(e, "Failed to fetch permissions."));
            } finally {
                if (!cancelled) setIsPermissionsLoading(false);
            }
        };

        fetchPermissions();
        return () => {
            cancelled = true;
        };
    }, []);

    const trimmedSearch = search.trim().toLowerCase();
    const visiblePermissions = useMemo(() => {
        if (!trimmedSearch) return permissions;
        return permissions.filter(
            (permission) =>
                permissionLabel(permission).toLowerCase().includes(trimmedSearch) ||
                (permission?.category ?? "").toLowerCase().includes(trimmedSearch),
        );
    }, [permissions, trimmedSearch]);

    const columns = useMemo(
        () => [
            {
                key: "name",
                label: "Role",
                sortable: false,
                className: "min-w-[160px]",
                style: { minWidth: 160 },
                render: (val) => <span className="font-medium text-text-primary whitespace-nowrap">{val ?? "—"}</span>,
            },
            ...visiblePermissions.map((permission) => ({
                key: permission.code,
                label: (
                    <span className="block w-[120px] whitespace-normal line-clamp-2 text-[11px] font-medium leading-tight">
                        {permissionLabel(permission)}
                    </span>
                ),
                title: permission.category
                    ? `${permissionLabel(permission)} · ${permission.category}`
                    : permissionLabel(permission),
                sortable: false,
                align: "center",
                render: (_val, row) =>
                    (row?.permissions ?? []).includes(permission.code) ? (
                        <Check size={13} className="inline text-tone-emerald" />
                    ) : (
                        <span className="text-text-muted">—</span>
                    ),
            })),
        ],
        [visiblePermissions],
    );

    return (
        <div className="flex flex-col gap-3">
            <p className="text-xs text-text-muted">
                The permissions each of this tenant&apos;s roles holds. View only: the tenant&apos;s admin manages roles.
            </p>

            <div className="flex items-center justify-between gap-3">
                <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder="Find a permission by name or category..."
                    className="max-w-xs"
                />

                <div className="flex items-center gap-3 text-[11px] text-text-muted whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                        <Check size={11} className="text-tone-emerald" /> Granted
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <span className="text-text-muted">—</span> Not granted
                    </span>
                </div>
            </div>

            {isPermissionsLoading ? (
                <div className="rounded-xl border border-border-primary px-4 py-10 text-center text-xs text-text-muted bg-background-secondary">
                    Loading permissions...
                </div>
            ) : (
                <DataTableNew
                    key="roles-permissions-matrix"
                    className="overflow-hidden rounded-xl border border-border-primary"
                    columns={columns}
                    rowKey="id"
                    api={RoleService.fetchRolesList}
                    extraParams={{ tenantId }}
                    dataPath="roles"
                    paginationLabel="roles"
                    sortable={false}
                    stickyHeader
                    stickyFirstColumn
                    maxHeight="70vh"
                />
            )}
        </div>
    );
};

export default RolesPanel;
